import React from 'react'
import { ConfirmCancelButtons } from 'src/components/Reusable/Buttons/ConfirmCancelButtons'
import {
  AnswerTextarea,
  FreeTextAnswerContainer,
  ReadOnlyAnswerText,
} from './Questionnaire.styles'

// Typed text is committed explicitly, unlike the select controls: onChange
// fires on confirm, not per keystroke, so a half-typed value is never saved and
// the caller's state (progress, conditional gating) doesn't churn while the
// responder is mid-sentence. The buttons only appear once the draft differs
// from what is saved, and Enter commits without reaching for them.
//
// Shared by the free-text answer and the supplemental text input, which needs
// the same check/cross affordance.
export const QuestionnaireFreeTextAnswer = ({
  value,
  draft,
  onDraftChange,
  placeholder,
  disabled,
  readOnly,
  onChange,
  id,
}: {
  value: string
  draft: string | undefined
  onDraftChange: (value: string) => void
  placeholder: string
  disabled: boolean
  readOnly: boolean
  onChange: (value: string) => void
  id?: string
}): JSX.Element => {
  // No draft recorded means nothing pending, so the saved value is shown.
  const shown = draft ?? value
  const isDirty = draft !== undefined && draft !== value
  const canEdit = !readOnly && !disabled

  // Read-only surfaces show the answer as text. A disabled textarea still
  // looks like a field -- and an unanswered one, complete with "Answer
  // here ...", reads as a form waiting to be filled rather than a question
  // nobody answered.
  if (readOnly) {
    return (
      <FreeTextAnswerContainer>
        <ReadOnlyAnswerText>{value || '—'}</ReadOnlyAnswerText>
      </FreeTextAnswerContainer>
    )
  }

  return (
    <FreeTextAnswerContainer>
      <AnswerTextarea
        id={id}
        placeholder={placeholder}
        value={shown}
        disabled={disabled}
        readOnly={readOnly}
        onChange={(e) => onDraftChange(e.target.value)}
        // Enter commits, the way the confirm button does; Shift+Enter still
        // inserts a newline, since these answers are routinely multi-line.
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey && isDirty && canEdit) {
            e.preventDefault()
            onChange(draft)
          }
        }}
      />
      {isDirty && canEdit && (
        <ConfirmCancelButtons
          onConfirm={() => onChange(draft)}
          onCancel={() => onDraftChange(value)}
        />
      )}
    </FreeTextAnswerContainer>
  )
}
